import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigationType } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  defaultDropAnimationSideEffects,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
  type KeyboardSensorOptions,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { toast } from 'sonner';
import {
  createApplicationNote,
  getPipelineBoard,
  moveApplicationStage,
  sendRejectionEmail,
  type ApiApplicationCard,
  type ApiApplicationStage,
  updateApplicationNote,
} from '../../libs/api/pipelineService';
import { getApiErrorMessage } from '../../libs/api/errorHandler';
import {
  applyScrollAfterPaint,
  parsePipelineScrollSnapshot,
  PIPELINE_SCROLL_STORAGE_KEY,
  type PipelineScrollSnapshot,
} from '@/libs/navigation/routeScrollStorage';
import {
  syncCandidateInterviewCaches,
  syncCandidateNoteCaches,
  syncCandidateStageCaches,
} from '../candidates/candidateData';
import KanbanCard from './KanbanCard';
import KanbanColumn from './KanbanColumn';
import InterviewScheduleModal from './InterviewScheduleModal';
import PipelineFilterBar from './PipelineFilterBar';
import {
  createInterview,
  listInterviewers,
  listInterviews,
  updateInterview,
  updateInterviewResult,
  type InterviewResult,
  type SaveInterviewPayload,
} from '../../libs/api/interviewService';
import {
  STAGES,
  collectUniqueValues,
  countCards,
  createEmptyColumns,
  filterPipelineColumns,
  type ColumnState,
  type PipelineCard,
} from './kanbanShared.ts';

const PIPELINE_BOARD_QUERY_KEY = ['pipeline-board'] as const;
const INTERACTIVE_DRAG_BLOCKERS_SELECTOR =
  'button, input, textarea, select, [contenteditable="true"], [contenteditable=""], [role="textbox"]';

type ClosestCapableTarget = {
  closest?: (selector: string) => unknown;
};

export const shouldHandleKanbanKeyboardDrag = (target: EventTarget | null) => {
  if (!target || typeof target !== 'object') {
    return true;
  }

  const maybeClosestCapableTarget = target as ClosestCapableTarget;
  if (typeof maybeClosestCapableTarget.closest !== 'function') {
    return true;
  }

  return !maybeClosestCapableTarget.closest(INTERACTIVE_DRAG_BLOCKERS_SELECTOR);
};

class KanbanKeyboardSensor extends KeyboardSensor {
  static override activators = KeyboardSensor.activators.map((activator) => ({
    ...activator,
    handler: (
      event: React.KeyboardEvent,
      options: KeyboardSensorOptions,
      context: Parameters<(typeof KeyboardSensor.activators)[number]['handler']>[2],
    ) => {
      if (!shouldHandleKanbanKeyboardDrag(event.target)) {
        return false;
      }

      return activator.handler(event, options, context);
    },
  }));
}

const mapCardFromApi = (
  card: ApiApplicationCard,
  fallbackStage: ApiApplicationStage,
): PipelineCard => ({
  id: card.id,
  applicationId: card.id,
  candidateId: card.candidate.id,
  name: card.candidate.full_name || 'Unknown Candidate',
  email: card.candidate.primary_email || '',
  emailSentAt: card.email_sent_at,
  canSendRejectionEmail: card.can_send_rejection_email,
  role: card.job_description.title,
  department: card.job_description.department,
  stage: card.current_stage || fallbackStage,
  score: card.ai_match_score ?? undefined,
  nextInterviewId: card.next_interview_id ?? null,
  nextInterviewResult: card.next_interview_result ?? null,
  nextInterviewRound: card.next_interview_round ?? null,
  appliedDate: new Date(card.applied_at).toLocaleDateString(),
  kanbanNote: card.latest_note_preview || '',
});

const buildColumnsFromResponse = (
  columns: Array<{ stage: ApiApplicationStage; items: ApiApplicationCard[] }>,
): ColumnState => {
  const next = createEmptyColumns();

  columns.forEach((column) => {
    next[column.stage] = column.items.map((item) => mapCardFromApi(item, column.stage));
  });

  return next;
};

const findCardById = (columns: ColumnState, cardId: string): PipelineCard | null => {
  for (const stage of STAGES) {
    const found = columns[stage].find((card) => card.id === cardId);
    if (found) return found;
  }

  return null;
};

const findCardStage = (columns: ColumnState, cardId: string): ApiApplicationStage | null => {
  for (const stage of STAGES) {
    if (columns[stage].some((card) => card.id === cardId)) {
      return stage;
    }
  }

  return null;
};

const moveCardLocally = (
  columns: ColumnState,
  cardId: string,
  toStage: ApiApplicationStage,
): ColumnState => {
  const fromStage = findCardStage(columns, cardId);
  const card = findCardById(columns, cardId);

  if (!fromStage || !card) {
    return columns;
  }

  if (fromStage === toStage) {
    return columns;
  }

  return {
    ...columns,
    [fromStage]: columns[fromStage].filter((item) => item.id !== cardId),
    [toStage]: [{ ...card, stage: toStage }, ...columns[toStage]],
  };
};

const moveCardInBoardResponse = (
  board: {
    columns: Array<{
      stage: ApiApplicationStage;
      count: number;
      items: ApiApplicationCard[];
    }>;
  },
  applicationId: string,
  toStage: ApiApplicationStage,
  updatedCard?: ApiApplicationCard,
) => {
  const columns = board.columns.map((column) => ({
    ...column,
    items: [...column.items],
  }));

  const sourceColumn = columns.find((column) =>
    column.items.some((item) => item.id === applicationId),
  );
  const targetColumn = columns.find((column) => column.stage === toStage);

  if (!sourceColumn || !targetColumn) {
    return board;
  }

  const sourceIndex = sourceColumn.items.findIndex((item) => item.id === applicationId);

  if (sourceIndex < 0) {
    return board;
  }

  const sourceCard = sourceColumn.items[sourceIndex];
  if (!sourceCard) {
    return board;
  }

  sourceColumn.items.splice(sourceIndex, 1);

  const cardForTarget: ApiApplicationCard = {
    ...sourceCard,
    ...updatedCard,
    current_stage: toStage,
  };

  targetColumn.items.unshift(cardForTarget);

  return {
    ...board,
    columns: columns.map((column) => ({
      ...column,
      count: column.items.length,
    })),
  };
};

const updateCardNoteInBoardResponse = (
  board: {
    columns: Array<{
      stage: ApiApplicationStage;
      count: number;
      items: ApiApplicationCard[];
    }>;
  },
  applicationId: string,
  noteText: string,
) => {
  const columns = board.columns.map((column) => ({
    ...column,
    items: column.items.map((item) =>
      item.id === applicationId
        ? {
            ...item,
            latest_note_preview: noteText,
          }
        : item,
    ),
  }));

  return {
    ...board,
    columns,
  };
};

const updateCardEmailSentInBoardResponse = (
  board: {
    columns: Array<{
      stage: ApiApplicationStage;
      count: number;
      items: ApiApplicationCard[];
    }>;
  },
  applicationId: string,
  sentAt: string,
) => {
  const columns = board.columns.map((column) => ({
    ...column,
    items: column.items.map((item) =>
      item.id === applicationId
        ? {
            ...item,
            email_sent_at: sentAt,
            can_send_rejection_email: false,
          }
        : item,
    ),
  }));

  return {
    ...board,
    columns,
  };
};

const updateCardInterviewResultInBoardResponse = (
  board: {
    columns: Array<{
      stage: ApiApplicationStage;
      count: number;
      items: ApiApplicationCard[];
    }>;
  },
  applicationId: string,
  result: InterviewResult | null,
) => {
  const columns = board.columns.map((column) => ({
    ...column,
    items: column.items.map((item) =>
      item.id === applicationId
        ? {
            ...item,
            next_interview_result: result,
          }
        : item,
    ),
  }));

  return {
    ...board,
    columns,
  };
};

interface KanbanBoardProps {
  onSelectCandidate: (applicationId: string) => void;
}

const resolveStageFromOver = (
  over: DragOverEvent['over'] | DragEndEvent['over'],
  columns: ColumnState,
): ApiApplicationStage | null => {
  if (!over) return null;

  const overType = over.data.current?.type;

  if (overType === 'Column') {
    return (over.data.current?.stage as ApiApplicationStage) ?? null;
  }

  if (overType === 'Candidate') {
    return findCardStage(columns, over.id as string);
  }

  if (STAGES.includes(over.id as ApiApplicationStage)) {
    return over.id as ApiApplicationStage;
  }

  return null;
};

const KanbanBoard: React.FC<KanbanBoardProps> = ({ onSelectCandidate }) => {
  const queryClient = useQueryClient();
  const navType = useNavigationType();
  const boardScrollRef = useRef<HTMLDivElement>(null);
  const columnBodyRefs = useRef<Partial<Record<ApiApplicationStage, HTMLDivElement>>>({});
  const visibleByStageRef = useRef<Record<ApiApplicationStage, number>>({
    NEW: 8,
    SCREENING: 8,
    INTERVIEW: 8,
    OFFER: 8,
    HIRED: 8,
    REJECTED: 8,
  });
  const popVisibleRestoredRef = useRef(false);
  const popScrollAppliedRef = useRef(false);

  const [columns, setColumns] = useState<ColumnState>(createEmptyColumns());
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedJobTitles, setSelectedJobTitles] = useState<string[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [activeCandidate, setActiveCandidate] = useState<PipelineCard | null>(null);
  const [interviewCandidate, setInterviewCandidate] = useState<PipelineCard | null>(null);
  const [targetStage, setTargetStage] = useState<ApiApplicationStage | null>(null);
  const columnsRef = useRef(columns);
  columnsRef.current = columns;
  const [visibleByStage, setVisibleByStage] = useState<Record<ApiApplicationStage, number>>({
    NEW: 8,
    SCREENING: 8,
    INTERVIEW: 8,
    OFFER: 8,
    HIRED: 8,
    REJECTED: 8,
  });

  visibleByStageRef.current = visibleByStage;

  const registerColumnBody = useCallback(
    (stage: ApiApplicationStage, el: HTMLDivElement | null) => {
      if (el) {
        columnBodyRefs.current[stage] = el;
      } else {
        delete columnBodyRefs.current[stage];
      }
    },
    [],
  );

  const persistPipelineScrollSnapshot = () => {
    const board = boardScrollRef.current;
    const columnsScroll: Record<string, number> = {};
    for (const s of STAGES) {
      columnsScroll[s] = columnBodyRefs.current[s]?.scrollTop ?? 0;
    }
    const snapshot: PipelineScrollSnapshot = {
      v: 1,
      board: {
        scrollLeft: board?.scrollLeft ?? 0,
        scrollTop: board?.scrollTop ?? 0,
      },
      columns: columnsScroll,
      visibleByStage: { ...visibleByStageRef.current },
    };
    sessionStorage.setItem(PIPELINE_SCROLL_STORAGE_KEY, JSON.stringify(snapshot));
  };

  const handleOpenCandidateProfile = (applicationId: string) => {
    persistPipelineScrollSnapshot();
    onSelectCandidate(applicationId);
  };

  const handleScheduleInterview = (candidate: PipelineCard) => {
    setInterviewCandidate(candidate);
  };

  const interviewApplicationId = interviewCandidate?.applicationId ?? '';
  const { data: interviewsResponse, isLoading: isLoadingInterviews } = useQuery({
    queryKey: ['interviews', interviewApplicationId],
    queryFn: () => listInterviews(interviewApplicationId),
    enabled: Boolean(interviewApplicationId),
  });
  const { data: interviewersResponse } = useQuery({
    queryKey: ['interviewers'],
    queryFn: listInterviewers,
    enabled: interviewCandidate !== null,
    staleTime: 60_000,
  });
  const saveInterviewMutation = useMutation({
    mutationFn: ({
      payload,
      scheduleId,
    }: {
      payload: SaveInterviewPayload;
      scheduleId?: string;
    }) => {
      if (!interviewCandidate) throw new Error('Candidate is not selected');
      return scheduleId
        ? updateInterview(interviewCandidate.applicationId, scheduleId, payload)
        : createInterview(interviewCandidate.applicationId, payload);
    },
  });

  const handleSaveInterview = async (payload: SaveInterviewPayload, scheduleId?: string) => {
    if (!interviewCandidate) return;
    try {
      const schedule = await saveInterviewMutation.mutateAsync({ payload, scheduleId });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['interviews', interviewCandidate.applicationId],
        }),
        queryClient.invalidateQueries({ queryKey: PIPELINE_BOARD_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: ['notifications'] }),
        queryClient.invalidateQueries({ queryKey: ['candidates'] }),
      ]);
      toast.success(
        schedule.graph_sync_status === 'SYNCED'
          ? 'Interview scheduled and synced to Microsoft Teams'
          : 'Interview saved. Microsoft Teams sync needs attention.',
      );
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to save interview'));
      throw error;
    }
  };

  const {
    data: boardResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: PIPELINE_BOARD_QUERY_KEY,
    queryFn: () => getPipelineBoard(),
  });

  useEffect(() => {
    if (!boardResponse) return;
    setColumns(buildColumnsFromResponse(boardResponse.columns));
  }, [boardResponse]);

  useEffect(() => {
    if (!error) return;
    toast.error(getApiErrorMessage(error, 'Failed to load pipeline board'));
  }, [error]);

  useEffect(() => {
    if (navType === 'PUSH' || navType === 'REPLACE') {
      sessionStorage.removeItem(PIPELINE_SCROLL_STORAGE_KEY);
    }
  }, [navType]);

  useEffect(() => {
    if (navType !== 'POP') {
      popVisibleRestoredRef.current = false;
      popScrollAppliedRef.current = false;
      return;
    }
    if (isLoading) return;
    if (popVisibleRestoredRef.current) return;

    const snapshot = parsePipelineScrollSnapshot(
      sessionStorage.getItem(PIPELINE_SCROLL_STORAGE_KEY),
    );
    if (!snapshot) return;

    popVisibleRestoredRef.current = true;
    setVisibleByStage((prev) => {
      const next = { ...prev };
      for (const s of STAGES) {
        const raw = snapshot.visibleByStage[s];
        if (typeof raw === 'number' && Number.isFinite(raw) && raw >= 8) {
          next[s] = Math.floor(raw);
        }
      }
      return next;
    });
  }, [navType, isLoading, boardResponse]);

  useEffect(() => {
    if (navType !== 'POP') return;
    if (isLoading) return;
    if (!popVisibleRestoredRef.current) return;
    if (popScrollAppliedRef.current) return;

    const snapshot = parsePipelineScrollSnapshot(
      sessionStorage.getItem(PIPELINE_SCROLL_STORAGE_KEY),
    );
    if (!snapshot) return;

    popScrollAppliedRef.current = true;
    applyScrollAfterPaint(() => {
      const board = boardScrollRef.current;
      if (board) {
        board.scrollLeft = snapshot.board.scrollLeft;
        board.scrollTop = snapshot.board.scrollTop;
      }
      for (const s of STAGES) {
        const el = columnBodyRefs.current[s];
        const top = snapshot.columns[s];
        if (el != null && typeof top === 'number') {
          el.scrollTop = top;
        }
      }
    });
  }, [navType, isLoading, visibleByStage, columns]);

  const moveStageMutation = useMutation({
    mutationFn: ({
      applicationId,
      toStage,
    }: {
      applicationId: string;
      toStage: ApiApplicationStage;
    }) => moveApplicationStage(applicationId, { to_stage: toStage }),
  });

  const createNoteMutation = useMutation({
    mutationFn: ({ applicationId, noteText }: { applicationId: string; noteText: string }) =>
      createApplicationNote(applicationId, { note_text: noteText }),
  });

  const updateNoteMutation = useMutation({
    mutationFn: ({
      applicationId,
      noteId,
      noteText,
    }: {
      applicationId: string;
      noteId: string;
      noteText: string;
    }) => updateApplicationNote(applicationId, noteId, { note_text: noteText }),
  });

  const sendRejectionEmailMutation = useMutation({
    mutationFn: (applicationId: string) => sendRejectionEmail(applicationId),
  });

  const updateInterviewResultMutation = useMutation({
    mutationFn: ({
      applicationId,
      scheduleId,
      result,
    }: {
      applicationId: string;
      scheduleId: string;
      result: InterviewResult | null;
    }) => updateInterviewResult(applicationId, scheduleId, result),
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    }),
    useSensor(KanbanKeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Prefer pointer-within for natural feel, fall back to rect-intersection so
  // empty columns and columns that are partially scrolled off-screen still
  // receive the card reliably.
  const collisionDetection: CollisionDetection = (args) => {
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) return pointerCollisions;
    return rectIntersection(args);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const candidate = findCardById(columns, active.id as string);
    if (candidate) {
      setActiveCandidate(candidate);
      setTargetStage(candidate.stage);
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const stage = resolveStageFromOver(event.over, columnsRef.current);
    setTargetStage((prev) => (prev === stage ? prev : stage));
  };

  const persistStageMove = async (
    cardId: string,
    toStage: ApiApplicationStage,
    snapshot: ColumnState,
  ) => {
    const card = findCardById(snapshot, cardId);
    if (!card) return;

    try {
      const updatedCard = await moveStageMutation.mutateAsync({
        applicationId: card.applicationId,
        toStage,
      });

      syncCandidateStageCaches(
        queryClient,
        {
          applicationId: card.applicationId,
          candidateId: card.candidateId,
        },
        updatedCard.current_stage ?? toStage,
      );

      queryClient.setQueryData(PIPELINE_BOARD_QUERY_KEY, (previous: unknown) => {
        if (!previous || typeof previous !== 'object') {
          return previous;
        }

        const board = previous as {
          columns: Array<{
            stage: ApiApplicationStage;
            count: number;
            items: ApiApplicationCard[];
          }>;
        };

        return moveCardInBoardResponse(board, card.applicationId, toStage, updatedCard);
      });
    } catch (error) {
      setColumns(snapshot);
      toast.error(getApiErrorMessage(error, 'Failed to move candidate stage'));
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveCandidate(null);
    setTargetStage(null);
    if (!over) return;

    const activeId = active.id as string;
    const currentColumns = columnsRef.current;
    const activeCard = findCardById(currentColumns, activeId);
    if (!activeCard) return;

    const toStage = resolveStageFromOver(over, currentColumns);
    if (!toStage || activeCard.stage === toStage) return;

    const snapshot = currentColumns;
    setColumns((prev) => moveCardLocally(prev, activeId, toStage));
    void persistStageMove(activeId, toStage, snapshot);
  };

  const handleDragCancel = () => {
    setActiveCandidate(null);
    setTargetStage(null);
  };

  const handleMoveCandidate = async (cardId: string, direction: 'prev' | 'next') => {
    const card = findCardById(columns, cardId);
    if (!card) return;

    const currentIndex = STAGES.indexOf(card.stage);
    const nextIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
    if (nextIndex < 0 || nextIndex >= STAGES.length) return;
    const nextStage = STAGES[nextIndex];
    if (!nextStage) return;

    const snapshot = columns;
    setColumns((prev) => moveCardLocally(prev, cardId, nextStage));
    await persistStageMove(cardId, nextStage, snapshot);
  };

  const handleSaveCardNote = (candidateId: string, note: string) => {
    if (!note.trim()) {
      toast.error('Note must be between 1 and 500 characters.');
      return Promise.resolve();
    }

    if (note.length > 500) {
      toast.error('Note must be between 1 and 500 characters.');
      return Promise.resolve();
    }

    const snapshot = columns;
    const targetCard = findCardById(snapshot, candidateId);

    if (!targetCard) {
      return Promise.resolve();
    }

    setColumns((prev) => {
      const next = createEmptyColumns();

      STAGES.forEach((stage) => {
        next[stage] = prev[stage].map((item) =>
          item.id === candidateId ? { ...item, kanbanNote: note } : item,
        );
      });

      return next;
    });

    const persist = async () => {
      try {
        if (targetCard.noteId) {
          const updated = await updateNoteMutation.mutateAsync({
            applicationId: targetCard.applicationId,
            noteId: targetCard.noteId,
            noteText: note,
          });

          setColumns((prev) => {
            const next = createEmptyColumns();

            STAGES.forEach((stage) => {
              next[stage] = prev[stage].map((item) =>
                item.id === candidateId
                  ? {
                      ...item,
                      kanbanNote: updated.note_text,
                      noteId: updated.id,
                    }
                  : item,
              );
            });

            return next;
          });

          queryClient.setQueryData(PIPELINE_BOARD_QUERY_KEY, (previous: unknown) => {
            if (!previous || typeof previous !== 'object') {
              return previous;
            }

            const board = previous as {
              columns: Array<{
                stage: ApiApplicationStage;
                count: number;
                items: ApiApplicationCard[];
              }>;
            };

            return updateCardNoteInBoardResponse(
              board,
              targetCard.applicationId,
              updated.note_text,
            );
          });

          syncCandidateNoteCaches(
            queryClient,
            {
              applicationId: targetCard.applicationId,
              candidateId: targetCard.candidateId,
            },
            updated.note_text,
          );
        } else {
          const created = await createNoteMutation.mutateAsync({
            applicationId: targetCard.applicationId,
            noteText: note,
          });

          setColumns((prev) => {
            const next = createEmptyColumns();

            STAGES.forEach((stage) => {
              next[stage] = prev[stage].map((item) =>
                item.id === candidateId
                  ? {
                      ...item,
                      kanbanNote: created.note_text,
                      noteId: created.id,
                    }
                  : item,
              );
            });

            return next;
          });

          queryClient.setQueryData(PIPELINE_BOARD_QUERY_KEY, (previous: unknown) => {
            if (!previous || typeof previous !== 'object') {
              return previous;
            }

            const board = previous as {
              columns: Array<{
                stage: ApiApplicationStage;
                count: number;
                items: ApiApplicationCard[];
              }>;
            };

            return updateCardNoteInBoardResponse(
              board,
              targetCard.applicationId,
              created.note_text,
            );
          });

          syncCandidateNoteCaches(
            queryClient,
            {
              applicationId: targetCard.applicationId,
              candidateId: targetCard.candidateId,
            },
            created.note_text,
          );
        }

        toast.success('Note saved successfully');
      } catch (error) {
        setColumns(snapshot);
        toast.error(getApiErrorMessage(error, 'Failed to save note'));
      }
    };

    return persist();
  };

  const handleSendCandidateEmail = async (candidateId: string) => {
    const targetCard = findCardById(columns, candidateId);

    if (
      !targetCard ||
      !targetCard.email ||
      targetCard.emailSentAt ||
      targetCard.canSendRejectionEmail === false
    ) {
      return;
    }

    try {
      const emailResult = await sendRejectionEmailMutation.mutateAsync(targetCard.applicationId);

      setColumns((prev) => {
        const next = createEmptyColumns();

        STAGES.forEach((stage) => {
          next[stage] = prev[stage].map((item) =>
            item.id === candidateId
              ? {
                  ...item,
                  emailSentAt: emailResult.sent_at,
                  canSendRejectionEmail: false,
                }
              : item,
          );
        });

        return next;
      });

      queryClient.setQueryData(PIPELINE_BOARD_QUERY_KEY, (previous: unknown) => {
        if (!previous || typeof previous !== 'object') {
          return previous;
        }

        const board = previous as {
          columns: Array<{
            stage: ApiApplicationStage;
            count: number;
            items: ApiApplicationCard[];
          }>;
        };

        return updateCardEmailSentInBoardResponse(
          board,
          targetCard.applicationId,
          emailResult.sent_at,
        );
      });

      toast.success('Rejection email sent successfully');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to send rejection email'));
    }
  };

  const handleSetInterviewResult = async (candidateId: string, result: InterviewResult | null) => {
    const targetCard = findCardById(columns, candidateId);

    if (!targetCard?.nextInterviewId) {
      toast.error('No interview schedule found for this candidate');
      return;
    }

    const previousResult = targetCard.nextInterviewResult ?? null;
    if (previousResult === result) {
      return;
    }

    setColumns((prev) => {
      const next = createEmptyColumns();

      STAGES.forEach((stage) => {
        next[stage] = prev[stage].map((item) =>
          item.id === candidateId ? { ...item, nextInterviewResult: result } : item,
        );
      });

      return next;
    });

    try {
      const updatedSchedule = await updateInterviewResultMutation.mutateAsync({
        applicationId: targetCard.applicationId,
        scheduleId: targetCard.nextInterviewId,
        result,
      });

      const rejected = result === 'FAILED';

      queryClient.setQueryData(PIPELINE_BOARD_QUERY_KEY, (previous: unknown) => {
        if (!previous || typeof previous !== 'object') {
          return previous;
        }

        const board = previous as {
          columns: Array<{
            stage: ApiApplicationStage;
            count: number;
            items: ApiApplicationCard[];
          }>;
        };

        const withResult = updateCardInterviewResultInBoardResponse(
          board,
          targetCard.applicationId,
          result,
        );

        return rejected
          ? moveCardInBoardResponse(withResult, targetCard.applicationId, 'REJECTED')
          : withResult;
      });

      if (rejected) {
        setColumns((prev) => moveCardLocally(prev, candidateId, 'REJECTED'));
        syncCandidateStageCaches(
          queryClient,
          {
            applicationId: targetCard.applicationId,
            candidateId: targetCard.candidateId,
          },
          'REJECTED',
        );
      }

      syncCandidateInterviewCaches(
        queryClient,
        {
          applicationId: targetCard.applicationId,
          candidateId: targetCard.candidateId,
        },
        {
          durationMinutes: updatedSchedule.duration_minutes,
          result: updatedSchedule.interview_result,
          round: updatedSchedule.interview_round,
          startAt: updatedSchedule.start_at,
        },
      );

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['candidates'] }),
        queryClient.invalidateQueries({ queryKey: ['interviews', targetCard.applicationId] }),
      ]);
      toast.success(
        rejected
          ? 'Interview result updated. Candidate moved to Rejected.'
          : 'Interview result updated successfully.',
      );
    } catch (error) {
      setColumns((prev) => {
        const next = createEmptyColumns();

        STAGES.forEach((stage) => {
          next[stage] = prev[stage].map((item) =>
            item.id === candidateId ? { ...item, nextInterviewResult: previousResult } : item,
          );
        });

        return next;
      });
      toast.error(getApiErrorMessage(error, 'Failed to update interview result'));
    }
  };

  const handleLoadMore = (stage: ApiApplicationStage) => {
    setVisibleByStage((prev) => ({ ...prev, [stage]: (prev[stage] ?? 0) + 8 }));
  };

  const jobTitleOptions = collectUniqueValues(columns, (card) => card.role);
  const teamOptions = collectUniqueValues(columns, (card) => card.department);
  const filteredColumns = filterPipelineColumns(columns, {
    search: searchTerm,
    jobTitles: selectedJobTitles,
    teams: selectedTeams,
  });
  const totalCandidateCount = countCards(columns);
  const visibleCandidateCount = countCards(filteredColumns);

  const handleResetFilters = () => {
    setSearchTerm('');
    setSelectedJobTitles([]);
    setSelectedTeams([]);
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center rounded-xl border border-border-subtle bg-card p-6 text-sm text-text-subtle">
        Loading pipeline board...
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-border-subtle bg-card shadow-sm">
      <PipelineFilterBar
        search={searchTerm}
        onSearchChange={setSearchTerm}
        jobTitleOptions={jobTitleOptions}
        selectedJobTitles={selectedJobTitles}
        onJobTitlesChange={setSelectedJobTitles}
        teamOptions={teamOptions}
        selectedTeams={selectedTeams}
        onTeamsChange={setSelectedTeams}
        onReset={handleResetFilters}
        visibleCount={visibleCandidateCount}
        totalCount={totalCandidateCount}
      />
      <div
        ref={boardScrollRef}
        className="flex min-h-0 flex-1 overflow-x-auto min-[1800px]:overflow-x-hidden"
      >
        <DndContext
          sensors={sensors}
          collisionDetection={collisionDetection}
          measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <div className="flex h-full min-w-max gap-0 min-[1800px]:w-full min-[1800px]:min-w-0">
            {STAGES.map((stage) => {
              const stageCandidates = filteredColumns[stage] ?? [];
              const visibleCount = visibleByStage[stage] ?? 0;
              const isDropTarget =
                activeCandidate !== null &&
                targetStage === stage &&
                activeCandidate.stage !== stage;

              return (
                <KanbanColumn
                  key={stage}
                  id={stage}
                  candidates={stageCandidates.slice(0, visibleCount)}
                  hasMore={stageCandidates.length > visibleCount}
                  onLoadMore={handleLoadMore}
                  isDropTarget={isDropTarget}
                  onColumnBodyRef={registerColumnBody}
                  renderCard={(candidate) => (
                    <KanbanCard
                      key={candidate.id}
                      candidate={candidate}
                      onMove={handleMoveCandidate}
                      onSaveNote={handleSaveCardNote}
                      onSendEmail={handleSendCandidateEmail}
                      onScheduleInterview={handleScheduleInterview}
                      onSetInterviewResult={handleSetInterviewResult}
                      onSelect={handleOpenCandidateProfile}
                    />
                  )}
                />
              );
            })}
          </div>

          <DragOverlay
            dropAnimation={{
              duration: 180,
              easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
              sideEffects: defaultDropAnimationSideEffects({
                styles: {
                  active: {
                    opacity: '0.4',
                  },
                },
              }),
            }}
          >
            {activeCandidate ? (
              <div className="pointer-events-none w-72 rotate-2 rounded-xl border border-primary/60 bg-card p-4 shadow-2xl ring-2 ring-primary/20">
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-light text-sm font-bold text-primary">
                    {activeCandidate.name.charAt(0)}
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-base font-semibold text-text-main">
                      {activeCandidate.name}
                    </div>
                    <div className="truncate text-sm text-text-subtle">{activeCandidate.role}</div>
                  </div>
                </div>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
      <InterviewScheduleModal
        candidate={interviewCandidate}
        schedules={interviewsResponse?.items ?? []}
        interviewers={interviewersResponse?.items ?? []}
        loading={isLoadingInterviews}
        saving={saveInterviewMutation.isPending}
        open={interviewCandidate !== null}
        onOpenChange={(open) => {
          if (!open) setInterviewCandidate(null);
        }}
        onSave={handleSaveInterview}
      />
    </div>
  );
};

export default KanbanBoard;
